/**
 * ChatThread Component
 * 
 * Reusable chat/message thread component for Customer-Vendor communication.
 * Can be used in asset detail pages and repair detail pages.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { MessageSquare, Send, Trash2, X } from 'lucide-react';
import ImageAttachment, { AttachmentDisplay } from './ImageAttachment';

export default function ChatThread({ 
  assetId = null, 
  repairId = null,
  isOpen = false,
  onClose,
  title = 'Messages',
  compact = false // For embedded view vs modal view
}) {
  const { 
    getAssetMessages, 
    getRepairMessages, 
    sendMessage, 
    deleteMessage,
    getAssetUnreadCount,
    getRepairUnreadCount
  } = useApi();
  const { user } = useAuth();
  
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [error, setError] = useState(null);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom when new messages arrive
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Load messages
  const loadMessages = useCallback(async () => {
    if (!assetId && !repairId) return;
    if (!getAssetMessages || !getRepairMessages) return;
    
    try {
      setLoading(true);
      setError(null);
      
      let data;
      if (assetId) {
        data = await getAssetMessages(assetId);
      } else if (repairId) {
        data = await getRepairMessages(repairId);
      }
      
      setMessages(data || []);
      setUnreadCount(0); // Reset unread count after viewing
    } catch (err) {
      // Log error for debugging, especially for customer role issues
      console.error('Error loading messages:', err);
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to load messages';
      setError(errorMessage);
      setMessages([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, repairId]); // Don't include API functions to prevent infinite loops

  // Load unread count
  const loadUnreadCount = useCallback(async () => {
    if (!assetId && !repairId) return;
    if (!getAssetUnreadCount || !getRepairUnreadCount) return;
    
    try {
      let count = 0;
      if (assetId) {
        count = await getAssetUnreadCount(assetId);
      } else if (repairId) {
        count = await getRepairUnreadCount(repairId);
      }
      setUnreadCount(count);
    } catch (err) {
      // Silently handle errors to prevent console spam
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, repairId]); // Don't include API functions to prevent infinite loops

  // Load messages on mount and when IDs change
  useEffect(() => {
    if (isOpen && (assetId || repairId)) {
      loadMessages();
    } else if (!isOpen && (assetId || repairId)) {
      loadUnreadCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, assetId, repairId]); // loadMessages and loadUnreadCount intentionally excluded to prevent infinite loops

  // Also reload messages when assetId/repairId changes while open
  useEffect(() => {
    if (isOpen && (assetId || repairId)) {
      loadMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, repairId, isOpen]); // loadMessages intentionally excluded to prevent infinite loops

  // Scroll to bottom when messages change
  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen, scrollToBottom]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle send message
  const handleSend = async () => {
    if ((!newMessage.trim() && attachments.length === 0) || sending) return;
    
    try {
      setSending(true);
      const result = await sendMessage({
        assetId,
        repairId,
        messageText: newMessage.trim() || '(Image)',
        attachments
      });
      
      // Add new message to local state
      setMessages(prev => [...prev, result]);
      setNewMessage('');
      setAttachments([]);
      scrollToBottom();
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Handle delete message
  const handleDelete = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    
    try {
      await deleteMessage(messageId);
      setMessages(prev => prev.filter(m => m.id !== messageId));
    } catch (err) {
      console.error('Error deleting message:', err);
      setError('Failed to delete message');
    }
  };

  // Handle key press (Enter to send)
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Get role badge color
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'customer':
      case 'user':
        return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      case 'vendor':
        return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'admin':
      case 'manager':
        return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' }) + ' ' + 
             date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' +
           date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Check if message is from current user
  const isOwnMessage = (message) => {
    return message.sender_id === user?.id;
  };

  // Format sender name - capitalize and format nicely
  const formatSenderName = (message) => {
    // Try sender_name first (already formatted from backend)
    if (message.sender_name && message.sender_name !== 'Unknown') {
      // If it starts with "User " (from backend fallback), just return it as-is
      if (message.sender_name.startsWith('User ')) {
        return message.sender_name;
      }
      // Otherwise, format it nicely
      return message.sender_name
        .split(/[._-]/)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(' ');
    }
    
    // Fallback to email prefix, formatted nicely
    if (message.sender_email && !message.sender_email.startsWith('deleted_user_')) {
      const emailPrefix = message.sender_email.split('@')[0];
      if (emailPrefix) {
        return emailPrefix
          .split(/[._-]/)
          .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
          .join(' ');
      }
    }
    
    // Last resort: use sender_id shortened
    return 'User ' + (message.sender_id?.substring(0, 8) || 'Unknown');
  };

  // Compact toggle button for showing unread count
  if (!isOpen && compact) {
    return (
      <button
        onClick={onClose}
        className="relative flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg transition-colors"
      >
        <MessageSquare size={18} className="text-blue-400" />
        <span className="text-sm text-slate-300">Chat</span>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
    );
  }

  // Main chat thread UI
  const chatContent = (
    <div className={`flex flex-col ${compact ? 'h-[400px]' : 'h-full'}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-slate-900/80">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-500/20 rounded-lg">
            <MessageSquare size={18} className="text-blue-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">{title}</h3>
            <p className="text-xs text-slate-400">
              {messages.length} message{messages.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <X size={18} className="text-slate-400" />
          </button>
        )}
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-900/50">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-6 h-6 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
          </div>
        ) : error ? (
          <div className="text-center py-8 text-red-400">
            <p>{error}</p>
            <button 
              onClick={loadMessages}
              className="mt-2 text-sm text-blue-400 hover:underline"
            >
              Try again
            </button>
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageSquare size={32} className="text-slate-600" />
            </div>
            <p className="text-slate-400 text-sm">No messages yet</p>
            <p className="text-slate-500 text-xs mt-1">Start the conversation</p>
          </div>
        ) : (
          <>
            {messages.map((message, index) => {
              const isOwn = isOwnMessage(message);
              const showAvatar = index === 0 || 
                messages[index - 1]?.sender_id !== message.sender_id;

              return (
                <div 
                  key={message.id}
                  className={`flex ${isOwn ? 'justify-end' : 'justify-start'} ${showAvatar ? 'mt-4' : 'mt-1'}`}
                >
                  <div className={`max-w-[80%] ${isOwn ? 'order-1' : 'order-2'}`}>
                    {/* Sender info (only show if different from previous) */}
                    {showAvatar && (
                      <div className={`flex items-center gap-2 mb-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                        <span className="text-xs font-medium text-slate-400">
                          {formatSenderName(message)}
                        </span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${getRoleBadgeColor(message.sender_role)}`}>
                          {message.sender_role}
                        </span>
                      </div>
                    )}
                    
                    {/* Message bubble */}
                    <div 
                      className={`relative group px-4 py-2.5 rounded-2xl ${
                        isOwn 
                          ? 'bg-blue-600 text-white rounded-br-md' 
                          : 'bg-slate-800 text-slate-200 rounded-bl-md'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap break-words">
                        {message.message_text}
                      </p>
                      
                      {/* Attachments */}
                      {message.attachments && message.attachments.length > 0 && (
                        <AttachmentDisplay 
                          attachments={typeof message.attachments === 'string' ? JSON.parse(message.attachments) : message.attachments}
                          className="mt-2"
                        />
                      )}
                      
                      {/* Delete button */}
                      {isOwn && (
                        <button
                          onClick={() => handleDelete(message.id)}
                          className="absolute -right-8 top-1/2 -translate-y-1/2 p-1 opacity-0 group-hover:opacity-100 hover:bg-red-500/20 rounded transition-all"
                        >
                          <Trash2 size={14} className="text-red-400" />
                        </button>
                      )}
                    </div>
                    
                    {/* Timestamp */}
                    <div className={`text-[10px] text-slate-500 mt-1 ${isOwn ? 'text-right' : 'text-left'}`}>
                      {formatTime(message.created_at)}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="p-4 border-t border-slate-700 bg-slate-900/80 space-y-3">
        {/* Image Attachment */}
        <ImageAttachment
          onAttachmentsChange={setAttachments}
          attachments={attachments}
          maxFiles={5}
          uploadEndpoint="/api/messages/upload-attachment"
          metadata={{ assetId, repairId }}
        />
        
        {/* Message Input */}
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            disabled={sending}
            className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
          />
          <button
            onClick={handleSend}
            disabled={(!newMessage.trim() && attachments.length === 0) || sending}
            className="px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {sending ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            ) : (
              <Send size={18} />
            )}
          </button>
        </div>
        <p className="text-[10px] text-slate-500 text-center">
          Press Enter to send • Messages are visible to {assetId ? 'asset' : 'repair'} participants
        </p>
      </div>
    </div>
  );

  // If compact mode (embedded), return just the content
  if (compact && isOpen) {
    return (
      <div className="bg-slate-900 border border-slate-700 rounded-xl overflow-hidden shadow-xl">
        {chatContent}
      </div>
    );
  }

  // Full modal mode
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg h-[600px] shadow-2xl overflow-hidden">
        {chatContent}
      </div>
    </div>
  );
}

/**
 * Chat Toggle Button - Standalone button to open chat
 */
export function ChatToggle({ 
  assetId = null, 
  repairId = null, 
  size = 'md',
  className = ''
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const { getAssetUnreadCount, getRepairUnreadCount } = useApi();

  // Load unread count
  useEffect(() => {
    if (!getAssetUnreadCount || !getRepairUnreadCount) return;
    
    const loadCount = async () => {
      try {
        let count = 0;
        if (assetId) {
          count = await getAssetUnreadCount(assetId);
        } else if (repairId) {
          count = await getRepairUnreadCount(repairId);
        }
        setUnreadCount(count);
      } catch (err) {
        // Silently handle errors to prevent console spam
      }
    };
    loadCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, repairId]); // Don't include API functions to prevent infinite loops

  const sizeClasses = {
    sm: 'p-1.5',
    md: 'p-2',
    lg: 'p-3'
  };

  const iconSizes = {
    sm: 14,
    md: 18,
    lg: 22
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={`relative bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg transition-colors ${sizeClasses[size]} ${className}`}
        title="Open chat"
      >
        <MessageSquare size={iconSizes[size]} className="text-blue-400" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      <ChatThread
        assetId={assetId}
        repairId={repairId}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={assetId ? 'Asset Chat' : 'Repair Chat'}
      />
    </>
  );
}

